import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import MaskedLines from "@/components/landing/MaskedLines";

const MOUNTAIN_BG =
  "https://images.pexels.com/photos/19948266/pexels-photo-19948266.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const voices = [
  {
    quote:
      "The weight of the hoodie feels like an old book — substantial, considered. Three seasons in, it still looks like the day it arrived.",
    name: "Ines Marchetti",
    role: "Editor, MONOLITH Quarterly",
  },
  {
    quote:
      "I rarely wear logos. HueBleu is the exception, because there isn't one — just a quiet line of gold stitched inside the cuff.",
    name: "Tomás Whitfield",
    role: "Architect, Sydney",
  },
  {
    quote:
      "You can feel the mountains in the cloth. It sounds romantic until you put it on.",
    name: "Aria Bellweather",
    role: "Founder, Salt & Stone",
  },
];

export default function Testimonials() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-20%", "20%"]);

  return (
    <section
      ref={ref}
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-32 md:py-44 overflow-hidden"
    >
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 z-0 opacity-20"
      >
        <img
          src={MOUNTAIN_BG}
          alt=""
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1d] via-[#0a0f1d]/85 to-[#0a0f1d] z-10" />

      <div className="relative z-20 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-6 mb-20">
          <span className="label-overline col-span-12 md:col-span-3">
            04 · Voices
          </span>
          <MaskedLines
            as="h3"
            className="col-span-12 md:col-span-9 font-serif-display text-white text-4xl md:text-6xl font-light leading-[1.05]"
            lines={[
              "Quiet words from",
              <em key="l2" className="italic text-[#D4AF37]">the people who wear it.</em>,
            ]}
          />
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {voices.map((v, i) => (
            <motion.figure
              key={i}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.9, delay: i * 0.12 }}
              className={`relative col-span-12 md:col-span-6 lg:col-span-4 p-8 md:p-10 border border-white/10 bg-[#0a0f1d]/40 backdrop-blur-sm ${
                i === 1 ? "lg:translate-y-12" : ""
              }`}
            >
              <div className="font-serif-display text-[#D4AF37] text-6xl leading-none mb-4">
                "
              </div>
              <blockquote className="font-serif-display text-white text-xl md:text-[22px] leading-[1.5] italic font-light">
                {v.quote}
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-white/10">
                <div className="text-white text-sm tracking-wide">
                  {v.name}
                </div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] mt-1">
                  {v.role}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
